import { Box, Typography, CircularProgress } from '@material-ui/core'
import { useStyles } from '../styles'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { useMemo } from 'react'
import clsx from 'clsx'

const formatCurrency = value =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)

const StatCard = ({ label, value, subtitle, loading, classes, testId }) => (
  <Box className={classes.statCard}>
    <Typography className={classes.statLabel} component="div">
      {label}
    </Typography>
    {loading ? (
      <CircularProgress size={24} className={classes.statLoader} />
    ) : (
      <Typography
        className={classes.statValue}
        component="div"
        data-testid={testId}
      >
        {value}
      </Typography>
    )}
    <Typography className={classes.statSubtitle} component="div">
      {subtitle}
    </Typography>
  </Box>
)

export default function StudentStats({
  totalRevenueInCents,
  totalBookingCount,
  privateBookingCount,
  groupSessionCount,
  loading,
}) {
  const classes = useStyles()

  const [isStudentRevenueEnabled] = useFeatureFlags([
    flags.FLAG_STUDENT_REVENUE_STAT,
  ])

  const calculatedRevenue = useMemo(() => totalRevenueInCents / 100, [
    totalRevenueInCents,
  ])

  const stats = useMemo(() => {
    const items = [
      {
        key: 'total-booked',
        label: 'Total Booked',
        value: totalBookingCount ?? 0,
        subtitle: 'All-time sessions',
      },
      {
        key: 'private-sessions',
        label: 'Private Sessions',
        value: privateBookingCount ?? 0,
        subtitle: '1-on-1 lessons',
      },
      {
        key: 'group-events',
        label: 'Group Events',
        value: groupSessionCount ?? 0,
        subtitle: 'Clinics & workshops',
      },
    ]

    if (isStudentRevenueEnabled) {
      items.push({
        key: 'lifetime-value',
        label: 'Lifetime Value',
        value: formatCurrency(calculatedRevenue),
        subtitle: 'Revenue to date',
      })
    }

    return items
  }, [
    calculatedRevenue,
    groupSessionCount,
    isStudentRevenueEnabled,
    privateBookingCount,
    totalBookingCount,
  ])

  return (
    <Box className={classes.statsSectionWrapper}>
      <Box
        className={clsx(
          classes.statsMetricsGrid,
          stats.length === 3 && classes.statsMetricsGridThreeCol,
        )}
      >
        {stats.map(stat => (
          <StatCard
            key={stat.key}
            testId={`student-stat-${stat.key}`}
            label={stat.label}
            value={stat.value}
            subtitle={stat.subtitle}
            loading={loading}
            classes={classes}
          />
        ))}
      </Box>
    </Box>
  )
}
